// ------ ONCE A USER HAS MADE A BOOKING ------

// A booked reservation has a 'charges' array which may include city tax charges of type "CityTax" - used POST booking
// We use timeslices.totalGrossAmount (room charges only) + city tax charges because:
// - timeslices contain ONLY room/accommodation charges
// - services are included in the charges array but do NOT appear in timeslices
// - this separation allows us to show accurate room breakdown including city tax
export const calculatePlacedRoomReservationWithCityTaxInc = (reservation) => {
  const reservationCityTaxTotal = (reservation) => {
    return reservation.charges
      .filter((charge) => charge.type === "CityTax")
      .reduce((acc, cityTaxCharge) => acc + cityTaxCharge.amount.grossAmount, 0);
  };
  return (
    reservation.timeSlices.reduce((acc, timeSlice) => acc + timeSlice.totalGrossAmount.amount, 0) +
    reservationCityTaxTotal(reservation)
  );
};

// Sums the total of a single BOOKING (multiple reservations) - to be used POST USER BOOKING
// This sums all the charges in each reservation's CHARGES array which includes room, services, city tax, etc.
export const calculatePlacedBookingTotal = (bookingReservations) => {
  let total = 0.0;
  bookingReservations.forEach((reservation) => {
    // iterate through each reservation. in each reservation sum the amount.grossAmount in each element of the charges array
    reservation.charges.forEach((charge) => {
      total += charge.amount.grossAmount;
    });
  });
  return total;
};

// ------ PRIOR TO A USER BOOKING ------

// Sums the total city tax for an availability / room reservation as CityTax is not included in totalGrossAmount - used PRE USER BOOKING
const cityTaxesTotal = (reservation) => {
  return reservation.cityTaxes
    ? reservation.cityTaxes.reduce(
        (acc, cityTax) => acc + (cityTax.totalGrossAmount ? cityTax.totalGrossAmount.amount : 0),
        0.0,
      )
    : 0;
};

// Sums the total of all services in a booking
const calculateServicesTotal = (bookingReservations) => {
  var servicesTotalGrossAmount = 0.0;
  bookingReservations.forEach((reservation) => {
    if (reservation.services) {
      reservation.services.forEach((service) => {
        servicesTotalGrossAmount += service.totalAmount.grossAmount;
      });
    }
  });

  return servicesTotalGrossAmount;
};

// Sums the total of all room bookings in a reservation array
// Note: reservation.totalGrossAmount.amount already includes city tax (added by calcAvailabilityWithCityTaxInc)
// and any rate-included services (e.g. dog fee), so no further adjustments are needed
export const calculateRoomsTotal = (bookingReservations) => {
  return bookingReservations.reduce(
    (accumulator, reservation) => accumulator + reservation.totalGrossAmount.amount,
    0.0,
  );
};

// Adds city tax to each AVAILABILITY totalGrossAmount
export const calcAvailabilityWithCityTaxInc = (availabilities) =>
  availabilities.map((availability) => {
    const cityTaxTotalAmount = cityTaxesTotal(availability);
    return {
      ...availability,
      totalGrossAmount: {
        ...availability.totalGrossAmount,
        amount: availability.totalGrossAmount.amount + cityTaxTotalAmount,
      },
    };
  });

// Prepares an availability list for any bookable surface (room cards, details
// sidebar) so they behave identically:
//   1. Hide member-only rates when auth CTAs are non-functional (so member rates
//      cannot be booked when the user has no way to authenticate)
//   2. Fold city tax into totalGrossAmount, which calculateRoomsTotal trusts
export const filterBookableAvailabilities = (availabilities, authFunctional) => {
  const memberFiltered = availabilities.filter(
    (availability) => authFunctional || !availability?.ratePlan?.isMemberRate,
  );
  return calcAvailabilityWithCityTaxInc(memberFiltered);
};

// The booking total, including room prices, service prices, city tax (and VAT)
export const calculateBookingTotal = (bookingReservations) => {
  return calculateRoomsTotal(bookingReservations) + calculateServicesTotal(bookingReservations);
};

// The VAT portion of a single reservation
const calculateReservationTotalVat = (bookingReservation) => {
  // Room VAT from the base accommodation charge
  let vatTotal = bookingReservation.timeSlices.reduce(
    (acc, timeSlice) => acc + (timeSlice.baseAmount.grossAmount - timeSlice.baseAmount.netAmount),
    0,
  );

  // VAT from rate-included services (e.g. dog fee) — these are in totalGrossAmount
  // but not in baseAmount, so they need to be counted separately
  bookingReservation.timeSlices.forEach((timeSlice) => {
    if (timeSlice.includedServices) {
      vatTotal += timeSlice.includedServices.reduce(
        (acc, service) => acc + (service.amount.grossAmount - service.amount.netAmount),
        0,
      );
    }
  });

  // Add services VAT
  if (bookingReservation.services) {
    vatTotal += bookingReservation.services.reduce(
      (acc, service) => acc + (service.totalAmount.grossAmount - service.totalAmount.netAmount),
      0,
    );
  }

  // Add city tax VAT
  if (bookingReservation.cityTaxes && bookingReservation.cityTaxes.length > 0) {
    vatTotal += bookingReservation.cityTaxes.reduce((cityTaxAcc, cityTax) => {
      if (cityTax.dates && cityTax.dates.length > 0) {
        return (
          cityTaxAcc +
          cityTax.dates.reduce((dateAcc, dateEntry) => {
            if (dateEntry.amount && dateEntry.amount.grossAmount && dateEntry.amount.netAmount) {
              return dateAcc + (dateEntry.amount.grossAmount - dateEntry.amount.netAmount);
            }
            return dateAcc;
          }, 0)
        );
      }
      return cityTaxAcc;
    }, 0);
  }

  return vatTotal;
};

// The VAT portion of an array of reservations
export const calculateBookingTotalVat = (bookingReservations) => {
  return bookingReservations.reduce(
    (acc, reservation) => acc + calculateReservationTotalVat(reservation),
    0,
  );
};

// Calculates the total for a service, based on type and number of nights
export const calculateServicePrice = (service, count) => {
  const basePrice = service.service.defaultGrossPrice.amount;
  const numberOfNights = service.dates.length;

  return basePrice * numberOfNights * count;
};

// Gift cards

// Returns the value of gift card that can be redeemed
// ie the total gift card balance, or booking total (the smallest number)
export const calculateGiftCardRedemption = (bookingReservations, giftCard) => {
  const bookingTotal = calculateBookingTotal(bookingReservations);
  return giftCard ? Math.min(bookingTotal, giftCard.balance / 100.0) : 0;
};

// Returns the remaining balance to pay, minus any gift card value
export const calculateTotalToPay = (bookingReservations, giftCard) => {
  // Need to round this to 2 decimals - if we do 75 - 59.99, chrome gives 15.0099
  // Convert to pennies to avoid floating point rounding errors
  const bookingTotal_100 = calculateBookingTotal(bookingReservations) * 100;
  const giftCardTotal_100 = calculateGiftCardRedemption(bookingReservations, giftCard) * 100;

  return (bookingTotal_100 - giftCardTotal_100) / 100.0;
};
